import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { getOrganizationGpuJob } from "../api/organization-generation.api";
import { validOrganizationGenerationDetail } from "../api/organization-generation-return";
import { useAuthStore } from "../auth/authStore";

/** Read-only detail. Pin every call to its original login and keep credentials out of cache keys. */
export function useOrganizationGenerationDetail(app: "dashboard" | "backoffice", id: string) {
  const owner = useAuthStore((state) => state.user?.sub),
    token = useAuthStore((state) => state.token),
    role = useAuthStore((state) => state.user?.role);
  const session = useMemo(
    () => ({ owner, token, role, nonce: crypto.randomUUID() }),
    [owner, token, role],
  );
  const current = () => {
    const latest = useAuthStore.getState();
    return (
      !!session.owner &&
      !!session.token &&
      latest.user?.sub === session.owner &&
      latest.token === session.token &&
      latest.user.role === session.role
    );
  };
  const valid = validOrganizationGenerationDetail(`/generation-jobs/${id}`);
  const query = useQuery({
    queryKey: ["organization-generation-detail", app, id, session.nonce],
    enabled: valid && current(),
    retry: false,
    queryFn: async ({ signal }) => {
      if (!current() || !session.owner)
        throw new Error("생성을 요청한 로그인 계정을 확인해 주세요.");
      const job = await getOrganizationGpuJob(session.owner, id, signal);
      if (!current() || job.id !== id || job.app !== app || job.input.app !== app)
        throw new Error("요청한 생성 작업과 응답이 다릅니다.");
      return job;
    },
    refetchInterval: (state) =>
      !state.state.error && ["QUEUED", "RUNNING"].includes(state.state.data?.status ?? "")
        ? 3000
        : false,
  });
  return {
    job: current() && !query.isError ? query.data : undefined,
    state: !valid
      ? ("invalid" as const)
      : query.isError
        ? ("error" as const)
        : query.data && current()
          ? ("ready" as const)
          : ("loading" as const),
    refreshing: query.isFetching,
    refresh: () => {
      if (current() && valid) void query.refetch();
    },
  };
}
